import { Controller } from "@hotwired/stimulus"

// TikTok 予約投稿フォーム。
// IG の scheduled_post_controller とは独立(投稿種別・制約・入力項目が異なるため)。
// ファイルは送信時に ActiveStorage Direct Upload(data-direct-upload-url)で S3 へ直送される。
export default class extends Controller {
  static targets = [
    "postTypeContainer", "dropZone", "mediaList", "mediaItem", "uploadArea", "addMoreArea",
    "fileInput", "fileInputMore", "newMediaContainer", "mediaFieldTemplate", "mediaHint",
    "coverIndexField", "coverButton", "positionLabel",
    "titleWrapper", "title", "titleCount", "caption", "captionCount", "captionLimit", "photoOnly", "videoOnly",
    "thumbnailSection", "thumbnailRange", "thumbnailLabel", "thumbnailField",
    "scheduleFields", "scheduledAt",
    "draftButton", "primaryButton", "primaryLabel", "primaryIcon", "primaryHint",
    "previewFrame", "previewMedia", "previewCaption", "previewNav", "previewCounter", "previewCoverLabel"
  ]

  static values = {
    maxPhotos: Number,
    maxVideoSize: Number,
    maxImageSize: Number,
    minVideoDuration: Number,
    maxVideoDuration: Number,
    minDimension: Number,
    maxImageLongSide: Number,
    maxImageShortSide: Number,
    captionLimits: Object,
    remainingPosts: Number,
    publishConfirm: String,
    username: String
  }

  connect() {
    this.newIndex = 1000 // 既存メディアの index と衝突しないよう大きい値から採番する
    this.previewIndex = 0
    this.updatePostTypeUI()
    this.updateTitleCount()
    this.updateCaptionCount()
    this.updateTimingUI()
    this.initCover()
    this.updateMediaAreas()
    this.updateButtons()
    // renderPreview() より前に呼ぶ(range の初期化前に changeThumbnailOffset が走ると hidden が 0 に潰れる)
    this.initThumbnailRange()
    this.renderPreview()
    this.initSortable()
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
    this.mediaItemTargets.forEach((item) => {
      if (item.dataset.previewUrl) URL.revokeObjectURL(item.dataset.previewUrl)
    })
  }

  // ---- 投稿種別 ----

  get postType() {
    const checked = this.element.querySelector('input[name="tiktok_scheduled_post[post_type]"]:checked')
    return checked ? checked.value : "video"
  }

  get isPhoto() {
    return this.postType === "photo"
  }

  get captionLimit() {
    return this.captionLimitsValue[this.postType] || 2200
  }

  changePostType(event) {
    // 種別を変えたら選択済みメディアは破棄する(動画と写真は混在できない)。無確認で消さない
    if (this.activeItems().length > 0 && !confirm("選択中のメディアは破棄されます。よろしいですか？")) {
      this.restorePostType(event.target)
      return
    }

    this.mediaItemTargets.forEach((item) => this.removeItem(item))
    this.previewIndex = 0
    this.updatePostTypeUI()
    this.updateMediaAreas()
    this.updateButtons()
    this.renderPreview()
  }

  // 破棄をキャンセルしたときは、選び直す前のラジオ(= 今チェックされていない方)に戻す
  restorePostType(selected) {
    const previous = Array.from(
      this.element.querySelectorAll('input[name="tiktok_scheduled_post[post_type]"]')
    ).find((radio) => radio !== selected)
    if (previous) previous.checked = true
  }

  updatePostTypeUI() {
    const isPhoto = this.isPhoto
    this.photoOnlyTargets.forEach((el) => el.classList.toggle("hidden", !isPhoto))
    this.videoOnlyTargets.forEach((el) => el.classList.toggle("hidden", isPhoto))
    if (this.hasTitleWrapperTarget) this.titleWrapperTarget.classList.toggle("hidden", !isPhoto)
    if (this.hasCaptionLimitTarget) this.captionLimitTarget.textContent = this.captionLimit

    const accept = isPhoto ? "image/jpeg,image/webp" : "video/mp4,video/quicktime,video/webm"
    this.fileInputTargets.concat(this.fileInputMoreTargets).forEach((input) => {
      input.accept = accept
      input.multiple = isPhoto
    })

    if (this.hasMediaHintTarget) {
      this.mediaHintTarget.textContent = isPhoto
        ? `JPG / WebP、各20MBまで、最大${this.maxPhotosValue}枚。${this.minDimensionValue}px以上、${this.maxImageShortSideValue}×${this.maxImageLongSideValue}(または${this.maxImageLongSideValue}×${this.maxImageShortSideValue})px以下`
        : `mp4 / mov / webm、1GBまで、${this.minVideoDurationValue}〜${this.maxVideoDurationValue}秒、幅・高さ${this.minDimensionValue}px以上`
    }

    if (this.sortable) this.sortable.option("disabled", !isPhoto)
  }

  // ---- 本文 ----

  updateTitleCount() {
    if (!this.hasTitleTarget || !this.hasTitleCountTarget) return

    this.titleCountTarget.textContent = [...this.titleTarget.value].length
  }

  updateCaptionCount() {
    if (!this.hasCaptionTarget) return

    const length = [...this.captionTarget.value].length
    if (this.hasCaptionCountTarget) {
      this.captionCountTarget.textContent = length
      this.captionCountTarget.classList.toggle("text-error", length > this.captionLimit)
    }
    if (this.hasPreviewCaptionTarget) this.previewCaptionTarget.textContent = this.captionTarget.value
    this.updateButtons()
  }

  // ---- メディア追加 ----

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-primary")
  }

  dragLeave() {
    this.dropZoneTarget.classList.remove("border-primary")
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-primary")
    this.processFiles(Array.from(event.dataTransfer.files))
  }

  addMedia(event) {
    this.processFiles(Array.from(event.target.files))
    event.target.value = ""
  }

  async processFiles(files) {
    const isPhoto = this.isPhoto
    const current = this.activeItems().length

    if (!isPhoto && (current >= 1 || files.length > 1)) {
      alert("動画投稿は動画1本のみです")
      return
    }
    if (isPhoto && current + files.length > this.maxPhotosValue) {
      alert(`写真は最大${this.maxPhotosValue}枚までです`)
      return
    }

    for (const file of files) {
      const error = this.validateFile(file)
      if (error) {
        alert(`${file.name}: ${error}`)
        continue
      }

      const meta = await this.readMetadata(file)
      this.appendMediaItem(file, meta)
    }

    this.updateMediaAreas()
    this.updateButtons()
    this.renderPreview()
  }

  validateFile(file) {
    if (this.isPhoto) {
      // 種別違いは「形式が違う」ではなくタブの切り替えを促す
      if (file.type.startsWith("video/")) return "写真モードでは動画を追加できません。動画タブに切り替えてください"
      if (!["image/jpeg", "image/webp"].includes(file.type)) return "JPGまたはWebPの画像を選択してください(PNGは使えません)"
      if (file.size > this.maxImageSizeValue) return "画像は20MB以下にしてください"
    } else {
      if (file.type.startsWith("image/")) return "動画モードでは写真を追加できません。写真タブに切り替えてください"
      if (!["video/mp4", "video/quicktime", "video/webm"].includes(file.type)) return "mp4 / mov / webm の動画を選択してください"
      if (file.size > this.maxVideoSizeValue) return "動画は1GB以下にしてください"
    }
    return null
  }

  // <video> / <img> でメタデータを読み、寸法・尺を取得する
  readMetadata(file) {
    const url = URL.createObjectURL(file)
    return new Promise((resolve) => {
      if (file.type.startsWith("video/")) {
        const video = document.createElement("video")
        video.preload = "metadata"
        video.onloadedmetadata = () => {
          resolve({ duration: video.duration, width: video.videoWidth, height: video.videoHeight, url })
        }
        video.onerror = () => resolve({ url })
        video.src = url
      } else {
        const image = new Image()
        image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight, url })
        image.onerror = () => resolve({ url })
        image.src = url
      }
    })
  }

  metadataError(meta) {
    if (meta.width && meta.height) {
      if (meta.width < this.minDimensionValue || meta.height < this.minDimensionValue) {
        return `幅・高さは${this.minDimensionValue}px以上にしてください`
      }
      if (this.isPhoto) {
        const long = Math.max(meta.width, meta.height)
        const short = Math.min(meta.width, meta.height)
        if (long > this.maxImageLongSideValue || short > this.maxImageShortSideValue) {
          return `画像は${this.maxImageShortSideValue}×${this.maxImageLongSideValue}(または${this.maxImageLongSideValue}×${this.maxImageShortSideValue})px以下にしてください`
        }
      }
    }
    if (meta.duration !== undefined) {
      if (meta.duration < this.minVideoDurationValue) return `動画は${this.minVideoDurationValue}秒以上必要です`
      if (meta.duration > this.maxVideoDurationValue) return `動画は${this.maxVideoDurationValue}秒以下にしてください`
    }
    return null
  }

  appendMediaItem(file, meta) {
    const error = this.metadataError(meta)
    if (error) {
      alert(`${file.name}: ${error}`)
      URL.revokeObjectURL(meta.url)
      return
    }

    const index = this.newIndex++
    const isVideo = file.type.startsWith("video/")

    // hidden field 群(テンプレートの name を新しい index に差し替える)
    const fragment = this.mediaFieldTemplateTarget.content.cloneNode(true)
    const group = fragment.querySelector(".media-field-group")
    group.querySelectorAll("input").forEach((input) => {
      input.name = input.name.replace(
        /\[tiktok_scheduled_post_media_attributes\]\[\d+\]/,
        `[tiktok_scheduled_post_media_attributes][${index}]`
      )
    })
    group.querySelector('[data-field="media_type"]').value = isVideo ? "video" : "image"
    group.querySelector('[data-field="duration"]').value = meta.duration === undefined ? "" : meta.duration
    group.querySelector('[data-field="width"]').value = meta.width === undefined ? "" : meta.width
    group.querySelector('[data-field="height"]').value = meta.height === undefined ? "" : meta.height

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    group.querySelector('[data-field="file"]').files = dataTransfer.files

    // 表示用アイテム(hidden field をこの中に置くと並び替えで一緒に動く)
    const item = document.createElement("div")
    item.className = "relative group media-item"
    item.dataset.tiktokScheduledPostTarget = "mediaItem"
    item.dataset.mediaType = isVideo ? "video" : "image"
    item.dataset.previewUrl = meta.url
    item.innerHTML = `
      <div class="aspect-[9/16] bg-base-200 rounded-lg overflow-hidden flex items-center justify-center">
        ${isVideo
          ? `<video class="w-full h-full object-contain" preload="metadata" src="${meta.url}"></video>`
          : `<img class="w-full h-full object-cover" src="${meta.url}" alt="">`}
      </div>
      <span class="sort-handle absolute top-1 left-1 btn btn-circle btn-xs cursor-grab active:cursor-grabbing">
        <span class="iconify lucide--grip-vertical size-3"></span>
      </span>
      <span class="absolute bottom-1 left-1 badge badge-xs badge-neutral" data-tiktok-scheduled-post-target="positionLabel"></span>
      <button type="button" class="absolute top-1 right-1 btn btn-circle btn-xs btn-error" data-action="click->tiktok-scheduled-post#removeMedia">
        <span class="iconify lucide--x size-3"></span>
      </button>
      <button type="button" class="absolute bottom-1 right-1 btn btn-xs" data-action="click->tiktok-scheduled-post#setCover" data-tiktok-scheduled-post-target="coverButton">カバー</button>`
    item.appendChild(group)
    this.mediaListTarget.appendChild(item)

    if (isVideo) this.setupThumbnailRange(meta.duration)
  }

  removeMedia(event) {
    this.removeItem(event.currentTarget.closest(".media-item"))
    this.previewIndex = 0
    this.updateMediaAreas()
    this.updateButtons()
    this.renderPreview()
  }

  // 保存済みメディアは _destroy を立てて隠し、未保存メディアは DOM ごと消す
  removeItem(item) {
    if (!item) return

    if (item.dataset.persisted === "true") {
      const destroyField = item.querySelector('[data-field="destroy"]')
      if (destroyField) destroyField.value = "1"
      item.classList.add("hidden")
      item.dataset.removed = "true"
    } else {
      if (item.dataset.previewUrl) URL.revokeObjectURL(item.dataset.previewUrl)
      item.remove()
    }
  }

  activeItems() {
    return this.mediaItemTargets.filter((item) => item.dataset.removed !== "true")
  }

  updateMediaAreas() {
    const items = this.activeItems()
    const isPhoto = this.isPhoto

    this.mediaListTarget.classList.toggle("hidden", items.length === 0)
    this.uploadAreaTarget.classList.toggle("hidden", items.length > 0)
    this.addMoreAreaTarget.classList.toggle("hidden", !(isPhoto && items.length > 0 && items.length < this.maxPhotosValue))
    this.updatePositions()
    // videoOnly の出し分け(updatePostTypeUI)より後に呼ぶ。順序が逆だと動画未選択でも表示されてしまう
    this.updateThumbnailSection()
  }

  // カバーは index ではなく要素(data-cover)で保持する。index だけだと並び替えで別の写真に移ってしまう
  initCover() {
    const items = this.activeItems()
    if (items.length === 0 || !this.hasCoverIndexFieldTarget) return

    const index = Math.min(Math.max(parseInt(this.coverIndexFieldTarget.value || "0", 10) || 0, 0), items.length - 1)
    this.markCover(items[index])
  }

  markCover(item) {
    this.mediaItemTargets.forEach((el) => delete el.dataset.cover)
    if (item) item.dataset.cover = "true"
  }

  // 表示順 = position。カバーの index は「カバーに指定された要素の現在位置」を書き戻す
  updatePositions() {
    const items = this.activeItems()

    // 並び替えと順番は写真投稿だけの概念なので、動画のときはハンドルとバッジを出さない
    const isPhoto = this.isPhoto

    items.forEach((item, index) => {
      const positionField = item.querySelector('[data-field="position"]')
      if (positionField) positionField.value = index
      const label = item.querySelector('[data-tiktok-scheduled-post-target="positionLabel"]')
      if (label) {
        label.textContent = index + 1
        label.classList.toggle("hidden", !isPhoto)
      }
      const handle = item.querySelector(".sort-handle")
      if (handle) handle.classList.toggle("hidden", !isPhoto)
    })

    let cover = items.findIndex((item) => item.dataset.cover === "true")
    if (cover < 0) {
      // カバーの要素が消えた(削除・種別変更)場合は先頭に付け直す
      this.markCover(items[0])
      cover = items.length > 0 ? 0 : -1
    }
    if (this.hasCoverIndexFieldTarget) this.coverIndexFieldTarget.value = Math.max(cover, 0)

    items.forEach((item, index) => {
      const button = item.querySelector('[data-tiktok-scheduled-post-target="coverButton"]')
      if (!button) return

      button.classList.toggle("btn-primary", index === cover)
      button.classList.toggle("hidden", !isPhoto)
    })
  }

  setCover(event) {
    this.markCover(event.currentTarget.closest(".media-item"))
    this.updatePositions()
  }

  async initSortable() {
    if (!this.hasMediaListTarget) return

    try {
      const { default: Sortable } = await import("sortablejs")
      this.sortable = Sortable.create(this.mediaListTarget, {
        handle: ".sort-handle",
        animation: 150,
        ghostClass: "opacity-50",
        disabled: !this.isPhoto,
        onEnd: () => {
          this.updatePositions()
          this.renderPreview()
        }
      })
    } catch (error) {
      console.error("Failed to initialize Sortable:", error)
    }
  }

  // ---- 動画カバー位置 ----

  // 動画が1本も無いと range は max=0 で動かせず「0.0 秒」だけが残るので、ブロックごと隠す
  updateThumbnailSection() {
    if (!this.hasThumbnailSectionTarget) return

    const hasVideo = !this.isPhoto && this.activeItems().some((item) => item.dataset.mediaType === "video")
    this.thumbnailSectionTarget.classList.toggle("hidden", !hasVideo)
  }

  // 既存の動画メディア(編集画面)の尺で range を初期化する
  initThumbnailRange() {
    const item = this.activeItems().find((el) => el.dataset.mediaType === "video")
    if (!item) return

    const durationField = item.querySelector('[data-field="duration"]')
    this.setupThumbnailRange(parseFloat(durationField ? durationField.value : ""))
  }

  setupThumbnailRange(duration) {
    if (!this.hasThumbnailRangeTarget || !duration) return

    this.thumbnailRangeTarget.max = Math.floor(duration * 2) / 2
    // max を広げたうえで hidden の値(サーバ描画値)を range に反映する。
    // 逆向き(range→hidden)だけだと、max が確定する前にクランプされた 0 を hidden に書き戻してしまう
    if (this.hasThumbnailFieldTarget) {
      const seconds = (parseInt(this.thumbnailFieldTarget.value || "0", 10) || 0) / 1000
      this.thumbnailRangeTarget.value = Math.min(seconds, parseFloat(this.thumbnailRangeTarget.max))
    }
    this.changeThumbnailOffset()
  }

  changeThumbnailOffset() {
    if (!this.hasThumbnailRangeTarget) return

    const max = parseFloat(this.thumbnailRangeTarget.max || "0")
    const seconds = parseFloat(this.thumbnailRangeTarget.value || "0")
    // 動画未選択・メタデータ未取得(max=0)のときは hidden を触らない(保存済みの値を 0 で潰さない)
    if (max > 0 && this.hasThumbnailFieldTarget) this.thumbnailFieldTarget.value = Math.round(seconds * 1000)
    // 表示書式はサーバ側(number_with_precision precision: 1)と揃える
    if (this.hasThumbnailLabelTarget) this.thumbnailLabelTarget.textContent = seconds.toFixed(1)
    if (this.hasPreviewCoverLabelTarget) this.previewCoverLabelTarget.textContent = `カバー位置 ${seconds.toFixed(1)}秒`

    // メタデータ未読込(readyState < 1)ではシークが捨てられるので触らない。
    // その場合は renderPreview() が張った loadedmetadata 側からここへ再入する
    const video = this.previewMediaTarget.querySelector("video")
    if (video && video.readyState >= 1) video.currentTime = seconds
  }

  // ---- プレビュー ----

  previewSrcFor(item) {
    if (item.dataset.previewUrl) return item.dataset.previewUrl

    const media = item.querySelector("[data-preview-media]")
    if (!media) return null
    if (media.currentSrc) return media.currentSrc

    const source = item.querySelector("[data-preview-media] source")
    return source ? source.getAttribute("src") : media.getAttribute("src")
  }

  renderPreview() {
    const items = this.activeItems()

    if (items.length === 0) {
      this.previewMediaTarget.innerHTML =
        '<div class="w-full h-full flex items-center justify-center text-white/50 text-sm">メディアを追加するとここに表示されます</div>'
      this.previewNavTarget.classList.add("hidden")
      this.previewCounterTarget.textContent = ""
      if (this.hasPreviewCoverLabelTarget) this.previewCoverLabelTarget.classList.add("hidden")
      return
    }

    this.previewIndex = Math.min(Math.max(this.previewIndex, 0), items.length - 1)
    const item = items[this.previewIndex]
    const src = this.previewSrcFor(item)

    const isVideo = item.dataset.mediaType === "video"
    this.previewMediaTarget.innerHTML = isVideo
      ? `<video class="w-full h-full object-contain" src="${src}" muted playsinline preload="metadata"></video>`
      : `<img class="w-full h-full object-cover" src="${src}" alt="">`

    this.previewNavTarget.classList.toggle("hidden", items.length < 2)
    this.previewCounterTarget.textContent = items.length > 1 ? `${this.previewIndex + 1} / ${items.length}` : ""
    if (this.hasPreviewCoverLabelTarget) this.previewCoverLabelTarget.classList.toggle("hidden", !isVideo)

    if (!isVideo) return

    // 生成直後の <video> は尺が未確定でシークが無視される。
    // 読込済みなら即シーク、未読込なら loadedmetadata を待ってからシークする
    const video = this.previewMediaTarget.querySelector("video")
    if (video && video.readyState < 1) {
      video.addEventListener("loadedmetadata", () => this.changeThumbnailOffset(), { once: true })
    } else {
      this.changeThumbnailOffset()
    }
  }

  prevSlide() {
    const count = this.activeItems().length
    if (count === 0) return

    this.previewIndex = (this.previewIndex - 1 + count) % count
    this.renderPreview()
  }

  nextSlide() {
    const count = this.activeItems().length
    if (count === 0) return

    this.previewIndex = (this.previewIndex + 1) % count
    this.renderPreview()
  }

  // ---- 公開のタイミング ----

  // timing はフォーム専用のラジオ(モデル属性ではない)。主ボタンの commit_action を切り替えるために使う
  get isSchedule() {
    const checked = this.element.querySelector('input[name="timing"]:checked')
    return checked ? checked.value === "schedule" : false
  }

  changeTiming() {
    this.updateTimingUI()
    this.updateButtons()
  }

  updateTimingUI() {
    const isSchedule = this.isSchedule

    if (this.hasScheduleFieldsTarget) this.scheduleFieldsTarget.classList.toggle("hidden", !isSchedule)
    // 「今すぐ」のときは disabled にして送信対象から外す(隠れた予約日時をサーバに送らない)
    if (this.hasScheduledAtTarget) this.scheduledAtTarget.disabled = !isSchedule
    if (this.hasPrimaryLabelTarget) this.primaryLabelTarget.textContent = isSchedule ? "予約する" : "今すぐ投稿"
    if (this.hasPrimaryIconTarget) {
      this.primaryIconTarget.classList.toggle("lucide--calendar-clock", isSchedule)
      this.primaryIconTarget.classList.toggle("lucide--send", !isSchedule)
    }
    if (this.hasPrimaryButtonTarget) {
      this.primaryButtonTarget.value = isSchedule ? "schedule" : "publish_now"
      this.primaryButtonTarget.classList.toggle("btn-info", isSchedule)
      this.primaryButtonTarget.classList.toggle("btn-primary", !isSchedule)
      // 確認ダイアログは即時投稿のときだけ出す
      if (isSchedule) {
        this.primaryButtonTarget.removeAttribute("data-turbo-confirm")
      } else {
        this.primaryButtonTarget.setAttribute("data-turbo-confirm", this.publishConfirmValue)
      }
    }
  }

  // ---- 送信制御 ----

  updateButtons() {
    const hasMedia = this.activeItems().length > 0
    const captionLength = this.hasCaptionTarget ? [...this.captionTarget.value].length : 0
    const captionOk = captionLength <= this.captionLimit
    const isSchedule = this.isSchedule
    // 「今すぐ」のとき日時欄は disabled だが value は読めるので、判定は予約時だけ使う
    const hasSchedule = this.hasScheduledAtTarget && this.scheduledAtTarget.value !== ""

    // メディア無し・キャプション超過は下書き保存も validation で落ちるため無効化する
    if (this.hasDraftButtonTarget) this.draftButtonTarget.disabled = !(hasMedia && captionOk)
    if (this.hasPrimaryButtonTarget) {
      this.primaryButtonTarget.disabled = isSchedule
        ? !(hasMedia && captionOk && hasSchedule)
        : !(hasMedia && captionOk && this.remainingPostsValue > 0)
    }

    // ボタンが押せない理由をその場で伝える(活性なら出さない)
    if (this.hasPrimaryHintTarget) {
      const reason = this.disabledReason({ hasMedia, captionOk, isSchedule, hasSchedule })
      this.primaryHintTarget.textContent = reason || ""
      this.primaryHintTarget.classList.toggle("hidden", reason === null)
    }
  }

  disabledReason({ hasMedia, captionOk, isSchedule, hasSchedule }) {
    if (!hasMedia) return "メディアを選択すると投稿できます"
    if (!captionOk) return "キャプションが上限を超えています"
    if (isSchedule && !hasSchedule) return "予約するには日時を入力してください"
    if (!isSchedule && this.remainingPostsValue <= 0) return "本日の投稿枠を使い切っています"
    return null
  }

  handleSubmit(event) {
    const action = event.submitter ? event.submitter.value : null
    if (action === "draft") return

    const count = this.activeItems().length
    if (!this.isPhoto && count !== 1) {
      alert("動画を1本選択してください")
      event.preventDefault()
      return
    }
    if (this.isPhoto && (count < 1 || count > this.maxPhotosValue)) {
      alert(`写真は1〜${this.maxPhotosValue}枚選択してください`)
      event.preventDefault()
      return
    }
    if (action === "schedule" && this.hasScheduledAtTarget && !this.scheduledAtTarget.value) {
      alert("予約日時を入力してください")
      event.preventDefault()
    }
  }
}
